// src/Dashboard/pages/teacher/GradeCN/utils/gradeCNDashboardUtils.js

export const DEFAULT_MALE_PHOTO = "/img/judoka_avatar.png";
export const DEFAULT_FEMALE_PHOTO = "/img/judokate_avatar.png";
export const DEFAULT_USER_PHOTO = "/img/user_avatar.jpg";
export const DEFAULT_BELT_PHOTO = "/img/belt/ceinture_inconnue.png";

export const DOMINANTE_OPTIONS = ["Compétition", "Technique"];

export function toAbsoluteUrl(url) {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (!value.startsWith("/uploads")) {
    return value;
  }

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

  return `${base}${value}`;
}

export function getDefaultPhoto(genre) {
  return genre === "Masculin" ? DEFAULT_MALE_PHOTO : DEFAULT_FEMALE_PHOTO;
}

export function getAdherentName(adherent = {}) {
  return `${String(adherent?.nom || "").toUpperCase()} ${
    adherent?.prenom || ""
  }`.trim();
}

export function getAdherentPhoto(adherent = {}) {
  const photo =
    adherent?.photo_url ||
    adherent?.photoURL ||
    adherent?.photo ||
    adherent?.avatar ||
    "";

  return toAbsoluteUrl(photo) || getDefaultPhoto(adherent?.genre);
}

export function formatDateFr(dateString) {
  if (!dateString) return "—";

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR");
}

export function getPreviousSeason(currentSeason) {
  const [startYear, endYear] = String(currentSeason || "")
    .split("-")
    .map(Number);

  if (!startYear || !endYear) return "";

  return `${startYear - 1}-${endYear - 1}`;
}

export function getNextSeason(currentSeason) {
  const [startYear, endYear] = String(currentSeason || "")
    .split("-")
    .map(Number);

  if (!startYear || !endYear) return "";

  return `${startYear + 1}-${endYear + 1}`;
}

export function buildSeasonOptions(activeSeason) {
  if (!activeSeason) return [];

  return [getPreviousSeason(activeSeason), activeSeason, getNextSeason(activeSeason)].filter(
    Boolean
  );
}

export function sortBelts(belts = []) {
  return [...belts].sort((a, b) => Number(a.id || 0) - Number(b.id || 0));
}

export function getBeltLabel(belt = {}) {
  return (
    belt?.nom ||
    belt?.label ||
    belt?.name ||
    belt?.belt_name ||
    belt?.couleur ||
    belt?.color ||
    "Ceinture"
  );
}

export function getBeltIdToName(belts = []) {
  const map = {};

  belts.forEach((belt) => {
    if (belt?.id != null) {
      map[belt.id] = getBeltLabel(belt);
    }
  });

  return map;
}

export function getBeltPhotoById(belts = [], beltId) {
  const belt = belts.find((item) => Number(item.id) === Number(beltId));

  if (!belt?.belt_url) return DEFAULT_BELT_PHOTO;

  return `/img/belt/${belt.belt_url}`;
}

export function getNextBeltPhotoById(belts = [], beltId) {
  const sorted = sortBelts(belts);
  const index = sorted.findIndex((item) => Number(item.id) === Number(beltId));

  if (index === -1 || index + 1 >= sorted.length) {
    return DEFAULT_BELT_PHOTO;
  }

  return sorted[index + 1]?.belt_url
    ? `/img/belt/${sorted[index + 1].belt_url}`
    : DEFAULT_BELT_PHOTO;
}

export function getNextBeltNameById(belts = [], beltId) {
  const sorted = sortBelts(belts);
  const index = sorted.findIndex((item) => Number(item.id) === Number(beltId));

  if (index === -1 || index + 1 >= sorted.length) {
    return "—";
  }

  return getBeltLabel(sorted[index + 1]);
}

export function getGradeUvBoolean(value) {
  return value === true || value === 1 || value === "1";
}

export function getUvCompletion(grade = {}) {
  const uv1 = getGradeUvBoolean(grade.uv1);
  const uv2 = getGradeUvBoolean(grade.uv2);
  const uv3 = getGradeUvBoolean(grade.uv3);
  const uv4 = getGradeUvBoolean(grade.uv4);

  const done = [uv1, uv2, uv3, uv4].filter(Boolean).length;
  const total = 4;
  const percent = Math.round((done / total) * 100);

  return {
    uv1,
    uv2,
    uv3,
    uv4,
    done,
    total,
    percent,
    complete: done === total,
  };
}

export function normalizeString(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

export function getCurrentBeltName({ grade, beltHistories, beltIdToName }) {
  const history = beltHistories?.[grade?.adherent_id];

  if (!history?.belt_id) return "";

  return beltIdToName?.[history.belt_id] || "";
}

export function getPresentBeltNames({
  grades = [],
  beltHistories = {},
  beltIdToName = {},
  season = "",
}) {
  const ids = new Set();

  grades.forEach((grade) => {
    if (season && grade?.Adherent?.saison_active !== season) return;

    const history = beltHistories?.[grade?.adherent_id];

    if (history?.belt_id != null) {
      ids.add(Number(history.belt_id));
    }
  });

  return Array.from(ids)
    .sort((a, b) => a - b)
    .map((id) => beltIdToName[id])
    .filter(Boolean);
}

export function filterGrades({
  grades = [],
  filters = {},
  beltHistories = {},
  beltIdToName = {},
}) {
  const query = normalizeString(filters.query);

  return grades.filter((grade) => {
    const adherent = grade?.Adherent || {};
    const fullName = normalizeString(
      `${adherent.nom || ""} ${adherent.prenom || ""}`
    );

    const currentBeltName = getCurrentBeltName({
      grade,
      beltHistories,
      beltIdToName,
    });

    const completion = getUvCompletion(grade);

    const matchesQuery = !query || fullName.includes(query);

    const matchesSeason =
      !filters.season || adherent?.saison_active === filters.season;

    const matchesBelt =
      !filters.beltName || currentBeltName === filters.beltName;

    const matchesDominante =
      filters.dominante === "All" || grade?.dominante === filters.dominante;

    const matchesProgress =
      filters.progress === "All" ||
      (filters.progress === "complete" && completion.complete) ||
      (filters.progress === "incomplete" && !completion.complete) ||
      (filters.progress === "uv1_missing" && !completion.uv1) ||
      (filters.progress === "uv2_missing" && !completion.uv2) ||
      (filters.progress === "uv3_missing" && !completion.uv3) ||
      (filters.progress === "uv4_missing" && !completion.uv4);

    return (
      matchesQuery &&
      matchesSeason &&
      matchesBelt &&
      matchesDominante &&
      matchesProgress
    );
  });
}

export function sortGrades(grades = [], sort = "name_asc") {
  return [...grades].sort((a, b) => {
    const nameA = normalizeString(
      `${a?.Adherent?.nom || ""} ${a?.Adherent?.prenom || ""}`
    );
    const nameB = normalizeString(
      `${b?.Adherent?.nom || ""} ${b?.Adherent?.prenom || ""}`
    );

    if (sort === "name_desc") return nameB.localeCompare(nameA, "fr");

    if (sort === "points_desc") {
      return Number(b?.uv3_points || 0) - Number(a?.uv3_points || 0);
    }

    if (sort === "points_asc") {
      return Number(a?.uv3_points || 0) - Number(b?.uv3_points || 0);
    }

    if (sort === "progress_desc") {
      return getUvCompletion(b).percent - getUvCompletion(a).percent;
    }

    if (sort === "progress_asc") {
      return getUvCompletion(a).percent - getUvCompletion(b).percent;
    }

    return nameA.localeCompare(nameB, "fr");
  });
}

export function calculateGradeCNStats(filteredGrades = [], allGrades = []) {
  const completeCount = filteredGrades.filter((grade) => getUvCompletion(grade).complete)
    .length;

  const incompleteCount = filteredGrades.length - completeCount;

  const totalUv3Points = filteredGrades.reduce(
    (sum, grade) => sum + Number(grade?.uv3_points || 0),
    0
  );

  const competitionCount = filteredGrades.filter(
    (grade) => grade?.dominante === "Compétition"
  ).length;

  const techniqueCount = filteredGrades.filter(
    (grade) => grade?.dominante === "Technique"
  ).length;

  return {
    totalAll: allGrades.length,
    totalFiltered: filteredGrades.length,
    completeCount,
    incompleteCount,
    totalUv3Points,
    competitionCount,
    techniqueCount,
  };
}

export function buildGradePayload(values = {}) {
  return {
    adherent_id: Number(values.adherent_id),
    dominante: values.dominante || "",
    uv1: !!values.uv1,
    uv2: !!values.uv2,
    uv3_points: values.uv3_points === "" ? 0 : Number(values.uv3_points || 0),
    uv3: !!values.uv3,
    uv4: !!values.uv4,
    observations: values.observations || "",
  };
}

export function normalizeGradeForForm(grade = {}) {
  return {
    adherent_id: grade?.adherent_id ? String(grade.adherent_id) : "",
    dominante: grade?.dominante || "",
    uv1: getGradeUvBoolean(grade?.uv1),
    uv2: getGradeUvBoolean(grade?.uv2),
    uv3_points: grade?.uv3_points ?? "",
    uv3: getGradeUvBoolean(grade?.uv3),
    uv4: getGradeUvBoolean(grade?.uv4),
    observations: grade?.observations || "",
  };
}

export function getEmptyGradeForm() {
  return {
    adherent_id: "",
    dominante: "",
    uv1: false,
    uv2: false,
    uv3_points: "",
    uv3: false,
    uv4: false,
    observations: "",
  };
}